import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FulfillmentStrategy } from '@kalachand/shared';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/StatusPill';
import { Modal } from '../../components/Modal';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';

interface Category {
  id: string;
  code: string;
  name: string;
  description: string | null;
  fulfillmentStrategy: FulfillmentStrategy;
  deliveryRequired: boolean;
  productionLeadTimeDays: number | null;
  supplierLeadTimeDays: number | null;
  tracksStockPerLocation: boolean;
  tracksUnitNumbers: boolean;
  fulfilFromWarehouseOnly: boolean;
  displayOrder: number;
  isActive: boolean;
  glRevenueAccountCode: string | null;
  glCogsAccountCode: string | null;
}

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  normalSide: 'DEBIT' | 'CREDIT';
}

const STRATEGIES: { key: FulfillmentStrategy; label: string; hint: string }[] = [
  {
    key: 'STOCK_AT_LOCATION',
    label: 'Stock at location',
    hint: 'We hold stock at the warehouse and/or shops (accessories, display mattresses)',
  },
  {
    key: 'MADE_TO_ORDER_FACTORY',
    label: 'Made to order — factory',
    hint: 'We manufacture on demand (local mattresses)',
  },
  {
    key: 'MADE_TO_ORDER_SUPPLIER',
    label: 'Made to order — supplier',
    hint: 'External supplier manufactures on demand (bed bases)',
  },
];

const strategyTone = (s: FulfillmentStrategy): 'positive' | 'info' | 'warning' | 'accent' => {
  switch (s) {
    case 'STOCK_AT_LOCATION':
      return 'positive';
    case 'MADE_TO_ORDER_FACTORY':
      return 'info';
    case 'MADE_TO_ORDER_SUPPLIER':
      return 'warning';
    default:
      return 'accent';
  }
};

export function CategoriesPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const qc = useQueryClient();
  const cats = useQuery({
    queryKey: ['cats-admin'],
    queryFn: () => apiGet<Category[]>('/categories', { includeInactive: 'true' }),
  });
  const deactivate = useMutation({
    mutationFn: (id: string) => apiPost(`/categories/${id}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cats-admin'] }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => apiPatch(`/categories/${id}`, { isActive: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cats-admin'] }),
  });

  return (
    <PageScaffold
      scroll={false}
      title="Categories"
      subtitle="Product groupings and their fulfilment strategy."
      actions={<Button onClick={() => setCreateOpen(true)}>+ Add category</Button>}
    >
      <DataTableCard>
        <table className="w-full text-sm">
          <thead className={STICKY_HEAD}>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
              <th className="px-6 py-2 font-medium">Code</th>
              <th className="px-6 py-2 font-medium">Name</th>
              <th className="px-6 py-2 font-medium">Strategy</th>
              <th className="px-6 py-2 font-medium">Delivery</th>
              <th className="px-6 py-2 font-medium">Lead time</th>
              <th className="px-6 py-2 font-medium">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(cats.data ?? []).map((c) => (
              <tr key={c.id} className="border-b border-rule-2 group hover:bg-paper-2">
                <td className="px-6 py-3 kc-mono">{c.code}</td>
                <td className="px-6 py-3 text-ink">
                  {c.name}
                  {c.description && (
                    <div className="text-xs text-ink-3 mt-0.5 max-w-xs">{c.description}</div>
                  )}
                </td>
                <td className="px-6 py-3">
                  <StatusPill tone={strategyTone(c.fulfillmentStrategy)}>
                    {STRATEGIES.find((s) => s.key === c.fulfillmentStrategy)?.label ??
                      c.fulfillmentStrategy}
                  </StatusPill>
                </td>
                <td className="px-6 py-3 text-ink-3">{c.deliveryRequired ? 'Yes' : 'No'}</td>
                <td className="px-6 py-3 text-ink-3 text-xs">
                  {c.productionLeadTimeDays
                    ? `${c.productionLeadTimeDays}d factory`
                    : c.supplierLeadTimeDays
                      ? `${c.supplierLeadTimeDays}d supplier`
                      : '—'}
                </td>
                <td className="px-6 py-3">
                  <StatusPill tone={c.isActive ? 'positive' : 'neutral'}>
                    {c.isActive ? 'active' : 'inactive'}
                  </StatusPill>
                </td>
                <td className="px-6 py-3 text-right">
                  <div className="flex gap-1 justify-end opacity-60 group-hover:opacity-100 transition">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                      Edit
                    </Button>
                    {c.isActive ? (
                      <Button size="sm" variant="ghost" onClick={() => deactivate.mutate(c.id)}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reactivate.mutate(c.id)}>
                        Reactivate
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableCard>

      {createOpen && <CategoryModal mode="create" onClose={() => setCreateOpen(false)} />}
      {editing && <CategoryModal mode="edit" category={editing} onClose={() => setEditing(null)} />}
    </PageScaffold>
  );
}

function CategoryModal({
  mode,
  category,
  onClose,
}: {
  mode: 'create' | 'edit';
  category?: Category;
  onClose: () => void;
}): JSX.Element {
  const [code, setCode] = useState(category?.code ?? '');
  const [name, setName] = useState(category?.name ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [strategy, setStrategy] = useState<FulfillmentStrategy>(
    category?.fulfillmentStrategy ?? 'STOCK_AT_LOCATION',
  );
  const [deliveryRequired, setDeliveryRequired] = useState(category?.deliveryRequired ?? false);
  const [leadDays, setLeadDays] = useState<string>(
    String(category?.productionLeadTimeDays ?? category?.supplierLeadTimeDays ?? ''),
  );
  const [tracksStockPerLocation, setTracksStockPerLocation] = useState(
    category?.tracksStockPerLocation ?? true,
  );
  const [tracksUnitNumbers, setTracksUnitNumbers] = useState(category?.tracksUnitNumbers ?? false);
  const [fulfilFromWarehouseOnly, setFulfilFromWarehouseOnly] = useState(
    category?.fulfilFromWarehouseOnly ?? false,
  );
  const [glRevenueAccountCode, setGlRevenueAccountCode] = useState(
    category?.glRevenueAccountCode ?? '',
  );
  const [glCogsAccountCode, setGlCogsAccountCode] = useState(category?.glCogsAccountCode ?? '');
  const qc = useQueryClient();
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => apiGet<Account[]>('/accounting/accounts'),
  });
  const revenueAccounts = (accounts.data ?? []).filter(
    (a) => a.type === 'REVENUE' && a.normalSide === 'CREDIT',
  );
  const cogsAccounts = (accounts.data ?? []).filter((a) => a.type === 'COGS');

  const submit = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name,
        description: description || null,
        fulfillmentStrategy: strategy,
        deliveryRequired,
        productionLeadTimeDays:
          strategy === 'MADE_TO_ORDER_FACTORY' && leadDays ? Number(leadDays) : null,
        supplierLeadTimeDays:
          strategy === 'MADE_TO_ORDER_SUPPLIER' && leadDays ? Number(leadDays) : null,
        tracksStockPerLocation,
        tracksUnitNumbers,
        fulfilFromWarehouseOnly,
        glRevenueAccountCode: glRevenueAccountCode || null,
        glCogsAccountCode: glCogsAccountCode || null,
      };
      if (mode === 'create') body.code = code.toUpperCase();
      return mode === 'create'
        ? apiPost('/categories', body)
        : apiPatch(`/categories/${category!.id}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cats-admin'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
  });

  const disabled = mode === 'create' ? !code || !name : !name;

  return (
    <Modal
      title={mode === 'create' ? 'New category' : `Edit · ${category?.code}`}
      subtitle="Strategy controls what happens to stock when an order is confirmed."
      size="xl"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={submit.isPending} disabled={disabled} onClick={() => submit.mutate()}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Code (uppercase)"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={16}
            required
            disabled={mode === 'edit'}
            hint={mode === 'edit' ? 'Code is permanent once created' : undefined}
          />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <Input
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="flex flex-col gap-2">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Fulfilment strategy
          </span>
          <div className="space-y-2">
            {STRATEGIES.map((s) => (
              <label
                key={s.key}
                className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition ${
                  strategy === s.key
                    ? 'border-accent bg-accent-soft/50'
                    : 'border-rule hover:border-accent/40'
                }`}
              >
                <input
                  type="radio"
                  name="strategy"
                  value={s.key}
                  checked={strategy === s.key}
                  onChange={() => setStrategy(s.key)}
                  className="accent-accent mt-1"
                />
                <div>
                  <div className="text-sm text-ink font-medium">{s.label}</div>
                  <div className="text-xs text-ink-3 mt-0.5">{s.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={deliveryRequired}
              onChange={(e) => setDeliveryRequired(e.target.checked)}
              className="accent-accent"
            />
            Delivery required
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={tracksStockPerLocation}
              onChange={(e) => setTracksStockPerLocation(e.target.checked)}
              className="accent-accent"
            />
            Track stock per location
          </label>
          {/* Numbered pieces — imported mattresses arrive with a number written
              on them by hand. Switching this on enables the Unit numbers page
              for this category's products. */}
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={tracksUnitNumbers}
              onChange={(e) => setTracksUnitNumbers(e.target.checked)}
              className="accent-accent"
            />
            Track unit numbers
          </label>
          {/* Central allocation — a delivery for this category comes from the
              warehouse only; if it's short, the order is reserved against the
              next arrival instead of taking a showroom's floor unit. */}
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={fulfilFromWarehouseOnly}
              onChange={(e) => setFulfilFromWarehouseOnly(e.target.checked)}
              className="accent-accent"
            />
            Deliveries from warehouse only
          </label>
        </div>
        {(strategy === 'MADE_TO_ORDER_FACTORY' || strategy === 'MADE_TO_ORDER_SUPPLIER') && (
          <Input
            label="Lead time (days)"
            type="number"
            min="0"
            value={leadDays}
            onChange={(e) => setLeadDays(e.target.value)}
          />
        )}
        <div className="border-t border-rule-2 pt-3 space-y-2">
          <div className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            GL accounts (sales posting)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Revenue account
              </span>
              <select
                className="kc-input"
                value={glRevenueAccountCode}
                onChange={(e) => setGlRevenueAccountCode(e.target.value)}
                disabled={accounts.isLoading}
              >
                <option value="">— none —</option>
                {revenueAccounts.map((a) => (
                  <option key={a.id} value={a.code}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                COGS account
              </span>
              <select
                className="kc-input"
                value={glCogsAccountCode}
                onChange={(e) => setGlCogsAccountCode(e.target.value)}
                disabled={accounts.isLoading}
              >
                <option value="">— none —</option>
                {cogsAccounts.map((a) => (
                  <option key={a.id} value={a.code}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-ink-3">
            Needed to post sales (and cost of goods on delivery) to the ledger. Leave blank only for
            categories that are never sold.
          </p>
        </div>
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}
